import * as React from "react";
import { useMemo, useState } from "react";

import { Body1, Spinner, Title1, tokens } from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  Checkmark24Regular,
  DismissCircle20Regular,
  Link20Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";

import { useStyles } from "./styles";
import ButtonComponent from "../../../../components/Button/Button";
import { AnalyzeResult, CitationType, ResolutionStatus } from "../../../../models/benchly.models";
import { CitationStorageService } from "../../../../service/citationStorage.service";
import { savePendingCitations } from "../../../../service/savePending.service";
import { useCitationStore } from "../../../../store/CitationStore";

/**
 * Helper to get overall status of a citation
 */
function getCitationStatus(citation: AnalyzeResult): ResolutionStatus {
  if (!citation.citations || citation.citations.length === 0) {
    return ResolutionStatus.NotFound;
  }
  
  if (citation.citations.every(c => c.status === ResolutionStatus.ValidatedAndCertified)) {
    return ResolutionStatus.ValidatedAndCertified;
  }
  
  if (citation.citations.some(c => c.status === ResolutionStatus.NeedsVerification)) {
    return ResolutionStatus.NeedsVerification;
  }
  
  if (citation.citations.some(c => c.status === ResolutionStatus.RequiresManualReview)) {
    return ResolutionStatus.RequiresManualReview;
  }
  
  return citation.citations[0].status;
}

const SavePending: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { citations: allCitations, setCitations } = useCitationStore();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Filter citations with pending changes (resolvedLawCase is defined)
  const pendingCitations = useMemo(() => {
    return allCitations.filter(
      (c) => getCitationStatus(c) === ResolutionStatus.NeedsVerification && c.resolvedLawCase !== undefined
    );
  }, [allCitations]);

  const selectedCitations = useMemo(() => {
    return pendingCitations.filter((c) => c.resolvedLawCase !== "none");
  }, [pendingCitations]);

  const ignoredCitations = useMemo(() => {
    return pendingCitations.filter((c) => c.resolvedLawCase === "none");
  }, [pendingCitations]);

  const handleCancel = () => {
    navigate("/resolving");
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      console.log("📍 Starting save of pending citations...");
      const startTime = performance.now();

      // Perform the save operation
      const { updatedCitations, removedIds } = await savePendingCitations(pendingCitations);

      console.log(
        `✅ Save completed in ${(performance.now() - startTime).toFixed(2)}ms`,
        `Updated: ${updatedCitations.length}, Removed: ${removedIds.length}`
      );

      // Update the context with the new state
      // Remove ignored citations from the list and update the rest
      const newCitations = allCitations
        .filter((c) => !removedIds.includes(c.documentReferenceId))
        .map((c) => {
          const updated = updatedCitations.find(
            (u) => u.documentReferenceId === c.documentReferenceId
          );
          return updated || c;
        });

      setCitations(newCitations);

      // Store the updated results
      await CitationStorageService.storeCitationResults(newCitations);

      // Navigate back to resolving or executive summary
      if (
        newCitations.filter((c) => getCitationStatus(c) === ResolutionStatus.NeedsVerification).length === 0
      ) {
        // No more citations to verify, go to executive summary
        navigate("/executive-summary");
      } else {
        navigate("/resolving");
      }
    } catch (error) {
      console.error("Error saving pending citations:", error);
      setSaveError(error instanceof Error ? error.message : "An error occurred while saving");
    } finally {
      setIsSaving(false);
    }
  };

  if (isSaving) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <Body1>Saving changes...</Body1>
          <Body1 style={{ color: tokens.colorNeutralForeground2, fontSize: "12px" }}>
            Updating document and applying hyperlinks...
          </Body1>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.stickyHeader}>
        <Title1>Review Changes</Title1>

        {/* Summary */}
        <div className={styles.summaryBox}>
          <div className={styles.summaryItem}>
            <Link20Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />
            <Body1>
              <strong>{selectedCitations.length}</strong> to be resolved
            </Body1>
          </div>
          <div className={styles.summaryItem}>
            <DismissCircle20Regular style={{ color: tokens.colorPaletteRedForeground1 }} />
            <Body1>
              <strong>{ignoredCitations.length}</strong> to be ignored
            </Body1>
          </div>
        </div>

        {saveError && (
          <div
            style={{
              padding: "8px 12px",
              backgroundColor: tokens.colorPaletteRedBackground2,
              border: `1px solid ${tokens.colorPaletteRedBorder1}`,
              borderRadius: "4px",
            }}
          >
            <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>{saveError}</Body1>
          </div>
        )}
      </div>

      {/* Changes list */}
      <div className={styles.scrollableContent}>
        <div className={styles.changesList}>
          {/* Selected citations */}
          {selectedCitations.map((citation) => (
            <SelectedCitationCard key={citation.documentReferenceId} citation={citation} />
          ))}

          {/* Ignored citations */}
          {ignoredCitations.map((citation) => (
            <IgnoredCitationCard key={citation.documentReferenceId} citation={citation} />
          ))}

          {pendingCitations.length === 0 && (
            <Body1
              style={{
                color: tokens.colorNeutralForeground2,
                textAlign: "center",
                padding: "20px",
              }}
            >
              No pending changes to save.
            </Body1>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className={styles.buttonContainer}>
        <ButtonComponent
          variant="outline-primary"
          onClick={handleCancel}
          icon={<ArrowLeft24Regular />}
        >
          Cancel
        </ButtonComponent>
        <ButtonComponent
          variant="primary"
          onClick={handleSaveChanges}
          disabled={pendingCitations.length === 0}
          icon={<Checkmark24Regular />}
        >
          Save Changes
        </ButtonComponent>
      </div>
    </div>
  );
};

interface CitationCardProps {
  citation: AnalyzeResult;
}

const SelectedCitationCard: React.FC<CitationCardProps> = ({ citation }) => {
  const styles = useStyles();
  const resolvedCase = citation.resolvedLawCase;

  if (resolvedCase === "none" || resolvedCase === undefined) return null;

  const isParallel = citation.citationType === CitationType.ParallelCites;
  const titleOnDocument = citation.citations?.[0]?.titleOnDocument;

  return (
    <div className={`${styles.changeItem} ${styles.changeItemSelected}`}>
      {/* Show title if available */}
      {titleOnDocument && (
        <div className={styles.citationText} style={{ fontStyle: "italic", marginBottom: "4px" }}>
          {titleOnDocument}
        </div>
      )}
      
      {/* Show all citations for parallel, or single citation */}
      {isParallel && citation.citations && citation.citations.length > 1 ? (
        <div>
          <div className={styles.citationText} style={{ fontWeight: "600", marginBottom: "4px" }}>
            Parallel Citations:
          </div>
          {citation.citations.map((cite, idx) => (
            <div key={idx} className={styles.citationText} style={{ marginLeft: "12px", fontSize: "13px" }}>
              • {cite.identifierOnDocument}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.citationText}>
          "{citation.citations?.[0]?.identifierOnDocument || "Unknown"}"
        </div>
      )}
      
      {/* Show what will happen */}
      <div className={styles.changeDescription} style={{ marginTop: "8px" }}>
        Will link to selected case:
      </div>
      <div className={styles.resolvedTo}>
        <strong>→</strong> {resolvedCase.identifierOnDocument}
        {resolvedCase.titleOnDocument && <span> - {resolvedCase.titleOnDocument}</span>}
      </div>
      
      {/* Hyperlink indicator */}
      {resolvedCase.foundDocs?.[0]?.hyperlink && (
        <div style={{ fontSize: "11px", color: tokens.colorBrandForeground1, marginTop: "4px" }}>
          <Link20Regular style={{ fontSize: "12px", marginRight: "4px" }} />
          Hyperlink will be applied to citation{isParallel ? "s" : ""}
        </div>
      )}
    </div>
  );
};

const IgnoredCitationCard: React.FC<CitationCardProps> = ({ citation }) => {
  const styles = useStyles();
  
  const isParallel = citation.citationType === CitationType.ParallelCites;
  const titleOnDocument = citation.citations?.[0]?.titleOnDocument;

  return (
    <div className={`${styles.changeItem} ${styles.changeItemIgnored}`}>
      {/* Show title if available */}
      {titleOnDocument && (
        <div className={styles.citationText} style={{ fontStyle: "italic", marginBottom: "4px" }}>
          {titleOnDocument}
        </div>
      )}
      
      {/* Show all citations for parallel, or single citation */}
      {isParallel && citation.citations && citation.citations.length > 1 ? (
        <div>
          <div className={styles.citationText} style={{ fontWeight: "600", marginBottom: "4px" }}>
            Parallel Citations:
          </div>
          {citation.citations.map((cite, idx) => (
            <div key={idx} className={styles.citationText} style={{ marginLeft: "12px", fontSize: "13px" }}>
              • {cite.identifierOnDocument}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.citationText}>
          "{citation.citations?.[0]?.identifierOnDocument || "Unknown"}"
        </div>
      )}
      
      <div className={styles.changeDescription}>
        <DismissCircle20Regular
          style={{ color: tokens.colorPaletteRedForeground1, marginRight: "4px", fontSize: "14px" }}
        />
        will be marked as ignored and removed from tracking
      </div>
    </div>
  );
};

export default SavePending;
